import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Container } from 'reactstrap';
import StyledButton from 'flaivy-react/components/StyledButton';
import _ from 'lodash';
import Link from 'next/link';
import Image from 'flaivy-react/components/Image';
import type { ShopConfig as OriginalShopConfig } from 'domain-model/types/supplier-gateway';

interface ShopConfig extends OriginalShopConfig {
  navbarConfig?: {
    version: string;
  };
}

interface Banner {
  showBanner?: boolean;
  fullWidth?: boolean;
  link?: string;
  imageLarge?: string;
  useLowerBanner?: boolean;
  heroUnderNavbar?: boolean;
  backgroundColor?: string;
  isFluid?: boolean;
  useVideo?: boolean;
  videoSrc?: string;
  heading?: string;
  headingColor?: string;
  subheading?: string;
  subheadingColor?: string;
  hasBannerButton?: boolean;
  buttonLink?: string;
  bannerButtonText?: string;
  showTextBanner?: boolean;
  textBannerBgColor?: string;
  textBannerTextColor?: string;
  textBannerField1?: string;
  textBannerField2?: string;
  textBannerField3?: string;
}

const FastShippingIcon = () => (
  <span className="ms-2 d-none d-md-inline-block">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="30px"
      viewBox="0 -960 960 960"
      width="30px"
      fill="white"
    >
      <path d="M240-160q-50 0-85-35t-35-85H40v-440q0-33 23.5-56.5T120-800h560v160h120l120 160v200h-80q0 50-35 85t-85 35q-50 0-85-35t-35-85H360q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T280-280q0-17-11.5-28.5T240-320q-17 0-28.5 11.5T200-280q0 17 11.5 28.5T240-240ZM120-360h32q17-18 39-29t49-11q27 0 49 11t39 29h272v-360H120v360Zm600 120q17 0 28.5-11.5T760-280q0-17-11.5-28.5T720-320q-17 0-28.5 11.5T680-280q0 17 11.5 28.5T720-240Zm-40-200h170l-90-120h-80v120ZM360-540Z" />
    </svg>
  </span>
);
const ThumbsUpIcon = () => (
  <span className="ms-2 pb-1 d-none d-md-inline-block">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="30px"
      width="30px"
      viewBox="0 -960 960 960"
      fill="white"
    >
      <path d="M720-120H280v-520l280-280 50 50q7 7 11.5 19t4.5 23v14l-44 174h258q32 0 56 24t24 56v80q0 7-2 15t-4 15L794-168q-9 20-30 34t-44 14Zm-360-80h360l120-280v-80H480l54-220-174 174v406Zm0-406v406-406Zm-80-34v80H160v360h120v80H80v-520h200Z" />
    </svg>
  </span>
);
const WeCareIcon = () => (
  <span className="ms-2 pb-2 d-none d-md-inline-block">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="30px"
      viewBox="0 -960 960 960"
      width="30px"
      fill="white"
    >
      <path d="M640-440 474-602q-31-30-52.5-66.5T400-748q0-55 38.5-93.5T532-880q32 0 60 13.5t48 36.5q20-23 48-36.5t60-13.5q55 0 93.5 38.5T880-748q0 43-21 79.5T807-602L640-440Zm0-112 109-107q19-19 35-40.5t16-48.5q0-22-15-37t-37-15q-14 0-26.5 5.5T700-778l-60 72-60-72q-9-11-21.5-16.5T532-800q-22 0-37 15t-15 37q0 27 16 48.5t35 40.5l109 107ZM280-220l278 76 238-74q-5-9-14.5-15.5T760-240H558q-27 0-43-2t-33-8l-93-31 22-78 81 27q17 5 40 8t68 4q0-11-6.5-21T578-354l-234-86h-64v220ZM40-80v-440h304q7 0 14 1.5t13 3.5l235 87q33 12 53.5 42t20.5 66h80q50 0 85 33t35 87v40L560-60l-280-78v58H40Zm80-80h80v-280h-80v280Zm520-546Z" />
    </svg>
  </span>
);

const ImageBanner = ({ image }: { image: string }) => (
  <Row className="g-0">
    <Col className="text-center">
      <Image
        src={image}
        resX={1070}
        resY={300}
        style={{ maxHeight: '300px', maxWidth: '1070px' }}
        className="w-100"
        alt="main banner"
      />
    </Col>
  </Row>
);

ImageBanner.propTypes = {
  image: PropTypes.string,
};

ImageBanner.defaultProps = {
  image: '',
};

const FullWidthBanner = ({
  banner,
  shopConfig,
}: {
  banner: Banner;
  shopConfig: ShopConfig;
}) => {
  const navbarRelatedClassNames = banner.heroUnderNavbar
    ? shopConfig?.navbarConfig?.version === 'v2'
      ? 'hero-under-navbar-v2'
      : 'hero-under-navbar-v1'
    : '';
  return (
    <>
      <div
        className={`hero ${navbarRelatedClassNames}
         hero--full-height hero--segment hero--centered`}
        style={{ backgroundColor: banner.backgroundColor }}
      >
        <div
          className={`hero__inner hero__inner--fixed-height
        ${banner.useLowerBanner ? 'hero__image--lower' : ''}
        ${banner.isFluid ? 'hero__inner--fluid' : ''}
        `}
        >
          {banner.useVideo && (
            <video
              src={banner.videoSrc}
              width="100%"
              autoPlay
              loop
              muted
              playsInline
              className="hero__video hero__content"
            />
          )}
          {!banner.useVideo && (
            <Image
              src={banner.imageLarge}
              alt="main banner"
              resX={1920}
              resY={1080}
              className={`hero__image hero__content `}
              style={{ width: '100%', minHeight: '100%' }}
            />
          )}
          <div className="text-center content-wrapper">
            {_.get(banner, 'heading') && (
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: banner.heading || '',
                }}
                style={{ color: banner.headingColor }}
                className={`banner-custom-heading ${
                  banner.useLowerBanner ? 'hero__image--lower' : ''
                }`}
              />
            )}
            {_.get(banner, 'subheading') && (
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: banner.subheading || '',
                }}
                style={{ color: banner.subheadingColor }} //fix color: ;
                className={`banner-custom-subheading ${
                  banner.useLowerBanner ? 'hero__image--lower' : ''
                }`}
              />
            )}
            {_.get(banner, 'hasBannerButton') && (
              <div
                className={`banner-button ${
                  banner.useLowerBanner ? 'hero__image--lower' : ''
                }`}
              >
                <Link href={_.get(banner, 'buttonLink', '/articles')}>
                  <StyledButton
                    color="light"
                    className="d-none d-md-inline-block btn-lg"
                  >
                    {banner.bannerButtonText}
                  </StyledButton>
                </Link>
                <Link href={_.get(banner, 'buttonLink', '/articles')}>
                  <StyledButton color="light" className="d-md-none">
                    {banner.bannerButtonText}
                  </StyledButton>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      {banner.showTextBanner && (
        <div
          className="usp-banner justify-content-center align-items-center"
          style={{
            background: banner.textBannerBgColor || 'white',
            color: banner.textBannerTextColor || 'black',
          }}
        >
          <Container className="usp-banner-inner">
            <Row className="justify-content-evenly align-items-top">
              <Col xs="auto" className="d-flex align-items-center">
                <span className="m-0 h6">{banner.textBannerField1}</span>
                <FastShippingIcon />
              </Col>

              <Col xs="auto" className="d-flex align-items-center">
                <span className="m-0 h6">{banner.textBannerField2}</span>
                <ThumbsUpIcon />
              </Col>

              <Col xs="auto" className="d-flex align-items-center">
                <span className="m-0 h6">{banner.textBannerField3}</span>
                <WeCareIcon />
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};
function BannerComponent({
  banner,
  shopConfig,
}: {
  banner: Banner;
  shopConfig: ShopConfig;
}) {
  const { imageLarge } = banner;

  return (
    <>
      {banner.showBanner && (
        <>
          {/* Small banner with or without Link */}
          {!banner.fullWidth && (
            <Container className="mt-4">
              {banner.link ? (
                <a href={banner.link}>
                  <ImageBanner image={imageLarge} />
                </a>
              ) : (
                <ImageBanner image={imageLarge} />
              )}
            </Container>
          )}

          {banner.fullWidth && (
            <FullWidthBanner banner={banner} shopConfig={shopConfig} />
          )}
        </>
      )}
    </>
  );
}
const mapStateToProps = ({ shopConfig }: { shopConfig: ShopConfig }) => ({
  shopConfig,
});
export default connect(mapStateToProps)(BannerComponent);
